import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PurchaseService, ShoppingCartService, ApplicationFacade } from '@ridango/components';
import {
    CategoryProduct,
    CompanionProfile,
    LocalProduct,
    NextButtonElement,
    Preferences,
    Product,
    ProductArea,
    ProductsInUrl,
    ProductTravellerType,
    SingleTicketGroupTicketTypeLimits
} from '@ridango/components/lib/purchase/purchase.model';
import { AppStartupService, BaseComponent, TranslateObjectFieldPipe, TravelMedia } from '@ridango/core';
import Env from '@ridango/core/env';
import { catchError, map, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { ActivationMode } from '@ridango/components/lib/purchase/purchase.model';
import { Temporal } from '@js-temporal/polyfill';
import { IonModal } from '@ionic/angular';
import { AmountAnnouncerService } from '@ridango/core/lib/services/amount-announcer.service';
import { ProductComponentUtil } from './product.component.util';

enum ButtonAction {
    ADD = 'Add',
    SUBTRACT = 'Subtract'
}

@Component({
    selector: 'gl-purchase-v2-ticket-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss']
})
export class ProductComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('startDateModal') startDateModal: IonModal;
    @ViewChild('endDateModal') endDateModal: IonModal;

    @Input() activationMode$: Observable<ActivationMode>;
    @Input() selectedStartingDate$: Observable<Temporal.PlainDate | null>;
    @Input() ActivationMode: typeof ActivationMode;
    @Input() minDate: string;
    @Output() activationModeChange = new EventEmitter<ActivationMode>();
    @Output() dateChange = new EventEmitter<Temporal.PlainDate>();
    @Output() nextStep = new EventEmitter<void>();

    protected readonly isNewAFC = this.env.IS_NEW_AFC;
    protected readonly assetsPath$ = this.applicationFacade.assetsPath$;
    protected readonly productComponentUtil = ProductComponentUtil;

    showProductInfo = false;
    productDescription = '';
    isHighContrast = false;

    private readonly today$ = new BehaviorSubject<Temporal.PlainDate>(Temporal.Now.plainDateISO());
    public readonly maxDateTemporal$: Observable<Temporal.PlainDate> = this.today$.pipe(
        map((today) => today.add({ days: 14 })),
        shareReplay(1)
    );
    public readonly maxDateISO$: Observable<string> = this.maxDateTemporal$.pipe(
        map((date) => date.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant().toString()),
        shareReplay(1)
    );

    public readonly is30DayTicket$: Observable<boolean> = combineLatest([
        this.purchaseService.isPurchaseDataLoading$
    ]).pipe(
        map(() => {
            const thirtyDaysInMinutes = 30 * 24 * 60;
            return this.productTravellerTypes.some(
                (product) => product.activeTicketDurationInMinutes === thirtyDaysInMinutes
            );
        }),
        startWith(false),
        shareReplay(1)
    );

    private showDateSelectionSubject$ = new BehaviorSubject<boolean>(false);
    public showDateSelection$ = this.showDateSelectionSubject$.asObservable();

    private selectedProductForDateSelectionSubject$ = new BehaviorSubject<ProductTravellerType | null>(null);
    public selectedProductForDateSelection$ = this.selectedProductForDateSelectionSubject$.asObservable();

    private dynamicDates$ = new BehaviorSubject<{
        start: Temporal.PlainDate;
        end: Temporal.PlainDate;
        min: Temporal.PlainDate;
        max: Temporal.PlainDate;
        maxEnd: Temporal.PlainDate;
    }>({
        start: Temporal.Now.plainDateISO(),
        end: Temporal.Now.plainDateISO().add({ days: 1 }),
        min: Temporal.Now.plainDateISO(),
        max: Temporal.Now.plainDateISO(),
        maxEnd: Temporal.Now.plainDateISO()
    });

    public dynamicTicketStartDate$ = this.dynamicDates$.pipe(
        map((dates) => dates.start),
        shareReplay(1)
    );

    public dynamicTicketEndDate$ = this.dynamicDates$.pipe(
        map((dates) => dates.end),
        shareReplay(1)
    );

    public dynamicTicketMinDate$ = this.dynamicDates$.pipe(
        map((dates) => dates.min),
        shareReplay(1)
    );

    public dynamicTicketMaxDate$ = this.dynamicDates$.pipe(
        map((dates) => dates.max),
        shareReplay(1)
    );

    public dynamicTicketMaxEndDate$ = this.dynamicDates$.pipe(
        map((dates) => dates.maxEnd),
        shareReplay(1)
    );

    public readonly dynamicTicketStartDateForDisplay$: Observable<Date> = this.dynamicTicketStartDate$.pipe(
        map(
            (plainDate) => new Date(plainDate.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant().epochMilliseconds)
        ),
        shareReplay(1)
    );

    public readonly dynamicTicketEndDateForDisplay$: Observable<Date> = this.dynamicTicketEndDate$.pipe(
        map(
            (plainDate) => new Date(plainDate.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant().epochMilliseconds)
        ),
        shareReplay(1)
    );

    public readonly dynamicTicketStartDateISO$: Observable<string> = this.dynamicTicketStartDate$.pipe(
        map((plainDate) => plainDate.toString()),
        shareReplay(1)
    );

    public readonly dynamicTicketEndDateISO$: Observable<string> = this.dynamicTicketEndDate$.pipe(
        map((plainDate) => plainDate.toString()),
        shareReplay(1)
    );

    public readonly dynamicTicketMinDateISO$: Observable<string> = this.dynamicTicketMinDate$.pipe(
        map((plainDate) => plainDate.toString()),
        shareReplay(1)
    );

    public readonly dynamicTicketMaxDateISO$: Observable<string> = this.dynamicTicketMaxDate$.pipe(
        map((plainDate) => plainDate.toString()),
        shareReplay(1)
    );

    public readonly dynamicTicketMaxEndDateISO$: Observable<string> = this.dynamicTicketMaxEndDate$.pipe(
        map((plainDate) => plainDate.toString()),
        shareReplay(1)
    );

    public dynamicTicketRangeTotalDays = 0;

    public locale: string;
    private highContrast$: Observable<boolean> = this.applicationFacade.highContrast$;

    constructor(
        private purchaseService: PurchaseService,
        private activatedRoute: ActivatedRoute,
        public appStartupService: AppStartupService,
        private router: Router,
        public translate: TranslateService,
        private shoppingCartService: ShoppingCartService,
        @Inject('ENV') public env: Env,
        protected translatePipe: TranslateObjectFieldPipe,
        private analyticsService: AnalyticsService,
        private amountAnnouncer: AmountAnnouncerService,
        private applicationFacade: ApplicationFacade
    ) {
        super();
    }

    @Output() nextButtonElement = new EventEmitter<NextButtonElement>();
    @Output() errorEvent = new EventEmitter<string>();
    @Input() productAreas: ProductArea[];

    @Input()
    set ticketsFromUrl(value: { ticketsFromUrl: string[] }) {
        this.parseDataFromUrl(value.ticketsFromUrl);
    }

    public productTravellerTypes: ProductTravellerType[] = [];
    private singleTicketGroupTicketTypeLimits: SingleTicketGroupTicketTypeLimits = {
        total: 0,
        perType: {}
    };

    public currency: string;
    public categoryFromPreviousStep: CategoryProduct;
    public chosenAreasFromPreviousStep: ProductArea[];
    public currentLanguage = this.translate.currentLang;
    public isLoading$ = this.purchaseService.isPurchaseDataLoading$;
    public fareDisplayText = '';
    public cartTickets = [];
    public selectedPreference: Preferences;

    public isAddButtonDisabled$ = this.purchaseService.ticketCount$.pipe(
        map((count) => count >= this.purchaseService.MAX_TICKETS_AMOUNT)
    );

    public isAddButtonDisabledForType$: Observable<Record<string, boolean>> = combineLatest([
        this.purchaseService.ticketCount$,
        this.purchaseService.productTravellerTypePurchaseCount$
    ]).pipe(
        map(([ticketCount, selectedCountPerType]) => {
            const isCartFull = ticketCount >= this.purchaseService.MAX_TICKETS_AMOUNT;
            if (this.productTravellerTypes?.[0]?.isSingleTicketGroup) {
                return this.buildGroupTicketDisabledMap(selectedCountPerType, isCartFull);
            }

            return this.buildPerProductDisabledMap(isCartFull);
        })
    );

    private buildPerProductDisabledMap(isCartFull: boolean): Record<string, boolean> {
        const disabledPerType: Record<string, boolean> = {};

        for (const product of this.productTravellerTypes) {
            disabledPerType[product.id] = isCartFull;
        }

        return disabledPerType;
    }

    private buildGroupTicketDisabledMap(
        selectedCountPerType: Record<string, number>,
        isCartFull: boolean
    ): Record<string, boolean> {
        const disabledPerType: Record<string, boolean> = {};
        const firstProduct = this.productTravellerTypes?.[0];

        if (!firstProduct?.id) {
            return disabledPerType;
        }

        const groupProductId = (firstProduct.id as string).split(':')[0];
        const totalGroupTicketCount = Object.values(selectedCountPerType).reduce((sum, val) => sum + val, 0);
        const isGroupTotalLimitReached = totalGroupTicketCount >= this.singleTicketGroupTicketTypeLimits.total;

        Object.keys(this.singleTicketGroupTicketTypeLimits.perType).forEach((code) => {
            const typeKey = `${groupProductId}:${code}`;
            const typeLimit = this.singleTicketGroupTicketTypeLimits.perType[code];
            const selectedCount = selectedCountPerType[code] ?? 0;

            disabledPerType[typeKey] = isCartFull || selectedCount >= typeLimit || isGroupTotalLimitReached;
        });

        return disabledPerType;
    }

    onActivationModeChange(event: CustomEvent): void {
        const mode = event.detail.value as ActivationMode;
        this.activationModeChange.emit(mode);
    }

    onDateChange(event: CustomEvent): void {
        const selectedDateString = event.detail.value;
        const selectedDate = Temporal.Instant.from(selectedDateString)
            .toZonedDateTimeISO(Temporal.Now.timeZoneId())
            .toPlainDate();
        const maxDate = this.today$.value.add({ days: 14 });

        if (Temporal.PlainDate.compare(selectedDate, maxDate) <= 0) {
            this.dateChange.emit(selectedDate);
        }
    }

    ngOnInit(): void {
        this.initSubscriptions();
        this.locale = this.translate.store.currentLang || this.translate.store.defaultLang;

        if (this.env.PURCHASE_FLOW.TICKET_STEPS.includes('preferences')) {
            this.purchaseService.currentPreference.pipe(takeUntil(this.componentDestroyed$)).subscribe((preference) => {
                this.selectedPreference = preference;
            });
            if (!this.selectedPreference) {
                return this.errorEvent.emit('NO_MEDIA_SELECTED');
            }
        }

        this.initProductValues();

        const productAreaIds = this.purchaseService.getStepDataIdsByStepName('product-area');
        this.chosenAreasFromPreviousStep = this.productAreas.filter((pa) => productAreaIds.includes(pa.id));
        this.currency = this.appStartupService.getCurrency();
        const categoryProduct = this.purchaseService.getStepDataIdsByStepName('category-product');

        this.fareDisplayText = this.calculateFareDisplayText();

        this.purchaseService
            .getCategoryProductById(categoryProduct[0], productAreaIds[0])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((categoryProduct) => {
                this.categoryFromPreviousStep = categoryProduct;
                const newFareDisplayText = this.calculateFareDisplayText();
                if (newFareDisplayText) {
                    this.fareDisplayText = newFareDisplayText;
                }
            });
        this.nextButtonElement.emit({
            content: { currency: this.currency, textTerm: 'CONTINUE_TO_CHECKOUT', amount: this.getTotalAmount() },
            disabled: !this.nextButtonEnabled()
        });
    }

    ngOnDestroy(): void {
        this.nextButtonElement.emit(null);
        this.today$.complete();
        this.showDateSelectionSubject$.complete();
        this.selectedProductForDateSelectionSubject$.complete();
        this.dynamicDates$.complete();
        super.ngOnDestroy();
    }

    async removeAmount(productId: number) {
        this.trackSelectAmountButtons(ButtonAction.SUBTRACT);
        const product = this.productTravellerTypes.find((userProfile) => userProfile.id === productId);

        const ticketsCountInCart = this.cartTickets.filter(
            (productCart) =>
                productCart.productId === product.id &&
                product.startZoneId === productCart.startZoneId &&
                productCart.cardId === this.selectedPreference?.cardId &&
                product.endZoneId === productCart.endZoneId
        ).length;

        if (product.amount <= ticketsCountInCart) {
            return;
        }

        product.amount--;

        this.purchaseService.reduceLocalTicketTypeAmount(product);
        await this.populateUrl();
        this.nextButtonElement.emit({
            content: { currency: this.currency, textTerm: 'CONTINUE_TO_CHECKOUT', amount: this.getTotalAmount() },
            disabled: !this.nextButtonEnabled()
        });
        await this.amountAnnouncer.announceAmountAndTotal(product?.amount ?? 0, this.getTotalAmount(), this.currency);
    }

    async addAmount(productId: number) {
        const product = this.productTravellerTypes.find((product) => product.id === productId);

        if (this.purchaseService.isDynamicLengthProduct(product)) {
            this.initializeDynamicDateRange();

            this.selectedProductForDateSelectionSubject$.next(product);
            this.showDateSelectionSubject$.next(true);

            this.purchaseService.setDateSelectionMode(true);

            this.nextButtonElement.emit({
                content: {
                    currency: this.currency,
                    textTerm: 'CONTINUE_TO_CHECKOUT'
                },
                disabled: false
            });

            return;
        }

        this.trackSelectAmountButtons(ButtonAction.ADD);
        product.amount++;

        const ticketsCountInCart = this.cartTickets.filter(
            (productCart) =>
                productCart.productId === product.id &&
                product.startZoneId === productCart.startZoneId &&
                productCart.cardId === this.selectedPreference?.cardId &&
                product.endZoneId === productCart.endZoneId
        ).length;

        const productIdWithoutParameters = (product.id as string).split(':')[0];
        this.purchaseService.increaseLocalProductTravellerTypeAmount({
            ...product,
            id: productIdWithoutParameters,
            amount: product.amount - ticketsCountInCart
        });
        await this.populateUrl();
        this.nextButtonElement.emit({
            content: { currency: this.currency, textTerm: 'CONTINUE_TO_CHECKOUT', amount: this.getTotalAmount() },
            disabled: !this.nextButtonEnabled()
        });
        await this.amountAnnouncer.announceAmountAndTotal(product?.amount ?? 0, this.getTotalAmount(), this.currency);
    }

    nextButtonEnabled(): boolean {
        return this.productTravellerTypes.some((product) => product.amount > 0);
    }

    private initProductValues() {
        this.purchaseService.setPurchaseDataLoading(true);
        const productAreaId = this.purchaseService.getStepDataIdsByStepName('areas')[0];
        const areaIds = this.purchaseService.getStepDataIdsByStepName('product-area');
        const categoryProductId = this.purchaseService.getStepDataIdsByStepName('category-product')[0];
        const currentCartId = this.shoppingCartService.getCartId();

        const filterIds = [];
        if (this.selectedPreference?.id && this.selectedPreference?.code !== TravelMedia.bank_card) {
            filterIds.push(this.selectedPreference.id.toString());
        } else if (productAreaId) {
            filterIds.push(productAreaId);
        }
        filterIds.push(...areaIds);
        if (categoryProductId) {
            filterIds.push(categoryProductId);
        }

        combineLatest([
            this.purchaseService.getProducts(
                filterIds.filter(Boolean),
                this.selectedPreference?.code,
                this.selectedPreference?.value
            ),
            currentCartId
                ? this.shoppingCartService.getShoppingCartStatus(currentCartId).pipe(
                      catchError((e) => {
                          console.error(e);
                          return of(null);
                      })
                  )
                : of(null)
        ])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                ([foundProducts, cart]) => {
                    if (foundProducts.length === 0) {
                        return this.errorEvent.emit('NOT_FIND_ANYTHING');
                    }
                    this.cartTickets = cart?.tickets || [];
                    const localProducts = this.purchaseService.getLocalProducts();
                    const productIdsInUrl = this.purchaseService.getStepDataIdsByStepName('product');
                    this.productTravellerTypes = this.buildTicketTypeFields(
                        productIdsInUrl,
                        foundProducts,
                        localProducts
                    );

                    if (this.env.IS_NEW_AFC) {
                        this.productDescription = this.productComponentUtil.getNewAFCproductDescription(
                            this.productTravellerTypes,
                            this.currentLanguage
                        );
                    }

                    const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
                    urlSegments.splice(urlSegments.indexOf('product') + 1);
                    this.router.navigate(
                        [
                            this.translate.currentLang,
                            'purchase-v2',
                            ...urlSegments,
                            ...this.productTravellerTypes
                                .filter((item) => item.amount > 0)
                                .reduce((acc, item) => [...acc, item.id, item.amount], [])
                        ],
                        {
                            replaceUrl: true
                        }
                    );
                    this.nextButtonElement.emit({
                        content: {
                            currency: this.currency,
                            textTerm: 'CONTINUE_TO_CHECKOUT',
                            amount: this.getTotalAmount()
                        },
                        disabled: !this.nextButtonEnabled()
                    });
                    this.updateAriaLabels();
                    this.purchaseService.setArrayLocalProducts(
                        this.productTravellerTypes.filter((product) =>
                            localProducts.some((item) => item.id === product.id)
                        )
                    );
                    this.purchaseService.updateCartTicketCount(this.cartTickets.length);
                    this.purchaseService.setPurchaseDataLoading(false);
                },
                (error) => {
                    console.error(error);
                },
                () => {
                    this.purchaseService.setPurchaseDataLoading(false);
                }
            );
    }

    private updateAriaLabels(): void {
        if (!this.productTravellerTypes?.length) return;

        this.productTravellerTypes = this.productTravellerTypes.map((ptt) => {
            const nameSource = this.env.IS_NEW_AFC ? ptt?.userProfiles?.[0]?.name : ptt?.name;

            const ariaLabel = this.amountAnnouncer.buildProductAriaLabel({
                name: this.translatePipe.transform(nameSource, null, this.currentLanguage) || '',
                description: ptt?.descriptionValue,
                amount: this.amountAnnouncer.resolveAmountValue(ptt?.price),
                priceCurrency: ptt?.price?.currency,
                fallbackCurrency: this.currency
            });

            return { ...ptt, ariaLabel };
        });
    }

    private buildTicketTypeFields(
        productsInUrl: ProductsInUrl,
        products: Product[],
        localProducts: LocalProduct[]
    ): Product[] {
        if (products.length === 1 && products[0].userProfiles?.[0]?.isGroup) {
            return this.buildGroupTicketTypeFields(productsInUrl, products[0], localProducts);
        } else {
            return this.buildSeparateTicketTypeFields(productsInUrl, products, localProducts);
        }
    }

    private buildSeparateTicketTypeFields(
        productsInUrl: ProductsInUrl,
        products: Product[],
        localProducts: LocalProduct[]
    ): Product[] {
        return products?.map((product) => {
            const indexUrlProduct = productsInUrl.indexOf(product.id as string);
            let amount = 0;

            const isDynamicProduct = this.purchaseService.isDynamicLengthProduct(product);
            if (isDynamicProduct) {
                amount = 0;
            } else if (
                this.cartTickets.find(
                    (productCart) =>
                        productCart.productId === product.id &&
                        product.startZoneId === productCart.startZoneId &&
                        product.endZoneId === productCart.endZoneId
                ) ||
                localProducts.find(
                    (localProduct) =>
                        localProduct.id === product.id &&
                        product.startZoneId === localProduct.startZoneId &&
                        product.endZoneId === localProduct.endZoneId
                )
            ) {
                amount =
                    this.cartTickets.filter(
                        (productCart) =>
                            productCart.productId === product.id &&
                            product.startZoneId === productCart.startZoneId &&
                            productCart.cardId === this.selectedPreference?.cardId
                    ).length +
                    (localProducts.find(
                        (localProduct) =>
                            localProduct.id === product.id &&
                            product.startZoneId === localProduct.startZoneId &&
                            product.endZoneId === localProduct.endZoneId &&
                            localProduct.cardId === this.selectedPreference?.cardId
                    )?.amount || 0);
            } else {
                amount = indexUrlProduct !== -1 ? +productsInUrl[indexUrlProduct + 1] : 0;
            }

            return {
                ...product,
                amount,
                price: this.purchaseService.defineProductPriceByActivationMethod(product),
                descriptionValue: this.purchaseService.getProductDescription(this.productAreas, product),
                isDynamicLength: this.purchaseService.isDynamicLengthProduct(product)
            };
        });
    }
    private buildGroupTicketTypeFields(
        productsInUrl: ProductsInUrl,
        product: Product,
        localProducts: LocalProduct[]
    ): Product[] {
        this.singleTicketGroupTicketTypeLimits.total = product.userProfiles[0].maxNumberOfPersons;

        return product.userProfiles[0].companionProfiles?.map((companionProfile: CompanionProfile) => {
            const productTravellerTypeParameterCode = companionProfile.dynamicProductParameters[0].code;
            const productTravellerTypeId = `${product.id}:${productTravellerTypeParameterCode}`;
            const indexUrlProduct = productsInUrl.indexOf(productTravellerTypeId);
            let amount = 0;
            if (
                this.cartTickets.find(
                    (productCart) =>
                        productCart.productId === product.id &&
                        product.startZoneId === productCart.startZoneId &&
                        product.endZoneId === productCart.endZoneId
                ) ||
                localProducts.find(
                    (localProduct) =>
                        localProduct.id === productTravellerTypeId &&
                        product.startZoneId === localProduct.startZoneId &&
                        product.endZoneId === localProduct.endZoneId
                )
            ) {
                amount =
                    this.cartTickets.filter(
                        (productCart) =>
                            productCart.productId === product.id &&
                            product.startZoneId === productCart.startZoneId &&
                            productCart.cardId === this.selectedPreference?.cardId
                    ).length +
                    (localProducts.find(
                        (localProduct) =>
                            localProduct.id == productTravellerTypeId &&
                            product.startZoneId === localProduct.startZoneId &&
                            product.endZoneId === localProduct.endZoneId &&
                            localProduct.cardId === this.selectedPreference?.cardId
                    )?.amount || 0);
            } else {
                amount = indexUrlProduct !== -1 ? +productsInUrl[indexUrlProduct + 1] : 0;
            }

            this.singleTicketGroupTicketTypeLimits.perType[productTravellerTypeParameterCode] =
                companionProfile.maxNumberOfPersons;

            return {
                ...product,
                isSingleTicketGroup: true,
                id: productTravellerTypeId,
                productTravellerTypeParameterCode,
                userProfiles: [companionProfile.userProfile],
                name: companionProfile.userProfile.name,
                descriptionValue:
                    this.purchaseService.getProductDescription(this.productAreas, product) +
                    this.translatePipe.transform(companionProfile.userProfile.name, null, this.translate.currentLang),
                amount,
                price: this.purchaseService.defineProductPriceByActivationMethod({
                    ...product,
                    price: companionProfile.perPersonPrice
                })
            };
        });
    }

    private parseDataFromUrl(dataFromUrl: string[]) {
        this.productTravellerTypes.forEach((obj) => {
            const index = dataFromUrl?.findIndex((id, i) => i % 2 === 0 && id == obj.id.toString());
            if (index !== -1) {
                obj.amount = Number(dataFromUrl[index + 1]);
            } else {
                obj.amount = 0;
            }
        });
    }

    private async populateUrl() {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        urlSegments.splice(urlSegments.indexOf('product') + 1);

        const filteredProducts = [...this.productTravellerTypes].filter((product) => product.amount > 0);
        const productsRoute = filteredProducts.map((item) => `${item.id}/${item.amount}`).join('/');
        await this.router.navigate([
            this.translate.currentLang,
            'purchase-v2',
            ...urlSegments,
            ...productsRoute.split('/')
        ]);
    }

    getTotalAmount() {
        return this.productTravellerTypes
            .filter((product) => product.amount > 0)
            .map((product) => {
                const priceAmount = product.price?.amount ?? 0;
                return product.amount * priceAmount;
            })
            .reduce((a, b) => a + b, 0);
    }

    private calculateFareDisplayText(): string {
        const categoryProductId = this.purchaseService.getStepDataIdsByStepName('category-product')[0];

        if (this.env.IS_NEW_AFC && categoryProductId.startsWith('fareProduct:')) {
            const baseFareCode = categoryProductId.split(':')[1];
            const baseFareProduct = this.purchaseService.getBaseFareProduct(baseFareCode);

            if (baseFareProduct && baseFareProduct.code === baseFareCode) {
                if (baseFareProduct.name && baseFareProduct.name.translations) {
                    const currentLang = this.translate.currentLang;
                    const translation = baseFareProduct.name.translations.find((t) => t.lang === currentLang);
                    if (translation) {
                        return translation.translation.trim();
                    }
                    if (baseFareProduct.name.translations.length > 0) {
                        return baseFareProduct.name.translations[0].translation.trim();
                    }
                } else {
                    return baseFareProduct.code;
                }
            }

            return baseFareCode;
        }

        const translationKey = categoryProductId.includes('30d') ? '30DAYS_WITHIN_TEXT' : '24H_WITHIN_TEXT';
        return this.translate.instant(translationKey);
    }

    private trackSelectAmountButtons(actionButton: ButtonAction) {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            name: `${actionButton} ticket`
        });
    }

    public onDynamicStartDateChange(event: CustomEvent): void {
        const selectedDateString = event.detail.value;
        const selectedDate = Temporal.PlainDate.from(selectedDateString);

        const currentDates = this.dynamicDates$.value;
        const maxPeriodDays = this.env.JOINT_TICKET_MAX_PERIOD;

        const defaultDays = this.env.TICKET?.DEFAULT_PERIOD_TICKET_DURATION_DAYS ?? 1;
        let newEndDate = currentDates.end;
        if (Temporal.PlainDate.compare(currentDates.end, selectedDate) < 0) {
            newEndDate = defaultDays <= 1 ? selectedDate : selectedDate.add({ days: defaultDays - 1 });
        }

        this.dynamicDates$.next({
            ...currentDates,
            start: selectedDate,
            end: newEndDate,
            maxEnd: selectedDate.add({ days: maxPeriodDays - 1 })
        });

        this.calculateDynamicPeriodDays(selectedDate, newEndDate);

        setTimeout(() => {
            if (this.startDateModal) {
                this.startDateModal.dismiss();
            }
        }, 100);

        this.updateDateSelectionNextButton();
    }

    public onDynamicEndDateChange(event: CustomEvent): void {
        const selectedDateString = event.detail.value;
        const endDate = Temporal.PlainDate.from(selectedDateString);

        const currentDates = this.dynamicDates$.value;
        const startDate = currentDates.start;

        const defaultDays = this.env.TICKET?.DEFAULT_PERIOD_TICKET_DURATION_DAYS ?? 1;
        const endBeforeStart = Temporal.PlainDate.compare(endDate, startDate) < 0;
        const fallbackEndDate = defaultDays <= 1 ? startDate : startDate.add({ days: defaultDays - 1 });
        const correctedEndDate = endBeforeStart ? fallbackEndDate : endDate;

        this.dynamicDates$.next({
            ...currentDates,
            end: correctedEndDate
        });

        this.calculateDynamicPeriodDays(startDate, correctedEndDate);

        setTimeout(() => {
            if (this.endDateModal) {
                this.endDateModal.dismiss();
            }
        }, 100);

        this.updateDateSelectionNextButton();
    }

    private async confirmDateSelection(): Promise<void> {
        const product = this.selectedProductForDateSelectionSubject$.value;
        const dates = this.dynamicDates$.value;
        const startDate = dates.start.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant().epochMilliseconds;
        const endDate = dates.end.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant().epochMilliseconds;

        this.trackSelectAmountButtons(ButtonAction.ADD);
        product.amount++;

        const ticketsCountInCart = this.cartTickets.filter(
            (productCart) =>
                productCart.productId === product.id &&
                product.startZoneId === productCart.startZoneId &&
                productCart.cardId === this.selectedPreference?.cardId &&
                product.endZoneId === productCart.endZoneId
        ).length;

        const productIdWithoutParameters = (product.id as string).split(':')[0];

        const productToSend = {
            ...product,
            id: productIdWithoutParameters,
            amount: product.amount - ticketsCountInCart,
            starting: new Date(startDate),
            ending: new Date(endDate)
        };

        this.purchaseService.increaseLocalProductTravellerTypeAmount(productToSend as any);
        await this.populateUrl();
        this.purchaseService.setDateSelectionMode(false);
        this.showDateSelectionSubject$.next(false);
        this.selectedProductForDateSelectionSubject$.next(null);

        this.nextButtonElement.emit({
            content: { currency: this.currency, textTerm: 'CONTINUE_TO_CHECKOUT', amount: this.getTotalAmount() },
            disabled: !this.nextButtonEnabled()
        });
        this.nextStep.emit();
    }

    private initializeDynamicDateRange(): void {
        const maxPeriodDays = this.env.JOINT_TICKET_MAX_PERIOD;
        const minDate = Temporal.Now.plainDateISO();
        const startDate = minDate;
        const defaultDays = this.env.TICKET?.DEFAULT_PERIOD_TICKET_DURATION_DAYS ?? 1;
        const endDate = defaultDays <= 1 ? minDate : minDate.add({ days: defaultDays - 1 });
        const maxDate = minDate.add({ days: maxPeriodDays });
        const maxEndDate = startDate.add({ days: maxPeriodDays - 1 });

        this.dynamicDates$.next({
            start: startDate,
            end: endDate,
            min: minDate,
            max: maxDate,
            maxEnd: maxEndDate
        });

        this.calculateDynamicPeriodDays(startDate, endDate);
    }

    private calculateDynamicPeriodDays(startDate: Temporal.PlainDate, endDate: Temporal.PlainDate): void {
        this.dynamicTicketRangeTotalDays = startDate.until(endDate).days + 1;
    }

    private updateDateSelectionNextButton(): void {
        const product = this.selectedProductForDateSelectionSubject$.value;
        if (product) {
            this.nextButtonElement.emit({
                content: {
                    currency: this.currency,
                    textTerm: 'CONTINUE_TO_CHECKOUT'
                },
                disabled: false
            });
        }
    }

    private initSubscriptions(): void {
        this.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.isHighContrast = value;
        });

        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
            this.locale = event.lang;
        });

        this.purchaseService.confirmDateSelection$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            if (this.showDateSelectionSubject$.value) {
                this.confirmDateSelection();
            }
        });
    }
}
